import localforage from "localforage";
import type { ParentComponent } from "solid-js";
import { createResource, Show, Suspense } from "solid-js";
import { CompanySidebarLayout } from "@/components/layout/CompanyLayout";
import { Loading } from "@/components/ui/loading";
import { ProtectedRoute } from "@/domains/auth/routes/protected-route";
import { EligibilityGuard } from "@/domains/billing/components/EligibilityGuard";
import { tryCatch } from "@/libs/try-catch";
import { FreelanceSearchProvider } from "../store/freelance-search-store";

/**
 * Holds the provider's mount until localforage's driver is up.
 *
 * This is NOT what orders hydration — localforage queues every method behind
 * `ready()` (`callWhenReady`), so `makePersisted`'s own `getItem` waits for the
 * driver with or without this gate. What the gate buys is a shorter window:
 * children mount *after* driver setup instead of during it, shortening the
 * window in which they can touch the still-empty store. That matters because
 * `makePersisted` discards the persisted value outright if the store is written
 * before its async init resolves (its `unchanged` latch) — children still see
 * an empty store on first render either way, so a consumer that writes at mount
 * time needs its own fallback (see `send-proposal/route.tsx`).
 *
 * Resolves truthy on rejection too (`tryCatch`), so a browser with no usable
 * driver mounts an empty, non-persisting store rather than sitting on the
 * loader forever.
 */
const waitForStorage = async () => {
  await tryCatch(localforage.ready());
  return true;
};

export const SearchLayout: ParentComponent = (props) => {
  const [storageReady] = createResource(waitForStorage);
  return (
    <ProtectedRoute>
      <CompanySidebarLayout>
        {/* Preventive guard covers every search child (A.2); blocks on degraded
            billing only (D6), so the per-offer application limit stays reactive. */}
        <EligibilityGuard action="active-search">
          <Suspense fallback={<Loading />}>
            <Show when={storageReady()} fallback={<Loading />}>
              <FreelanceSearchProvider>
                {props.children}
              </FreelanceSearchProvider>
            </Show>
          </Suspense>
        </EligibilityGuard>
      </CompanySidebarLayout>
    </ProtectedRoute>
  );
};
